import { Router } from '@angular/router'
import { ENV, Interceptor, LoggerService } from '@danangelus/ngx-app-core'
import { CookieService } from 'ngx-cookie-service'
import { SessionService } from './session.service'

const log = LoggerService.get('AuthenticationInterceptor')

/**
 * Applies SSOTicketId, X-Client and X-Page headers if the user is authenticated.
 * Also intercepts 401 responses to redirect to the login page instead of allowing
 * the browser to display its native credentials dialog.
 *
 * @author Dan Bennett
 */
export class AuthenticationInterceptor implements Interceptor {

    constructor(
        private router: Router,
        private sessionService: SessionService,
        private cookieService: CookieService,
    ) {
        this.patchFetch()
    }

    private excludedEndpoints: string[] = [
        'invalidate',
        'mfa/validate',
        'mfa/send-new-code',
        'load-environment',
        'login',
    ]

    canActivate(endpoint?: string) {
        return (!endpoint || !this.excludedEndpoints.includes(endpoint))
    }

    async call(req: Request): Promise<any | unknown | undefined> {
        log.trace('authenticationInterceptor', 'Applying values to request')

        const ssoTicketId = this.sessionService.user()?.ssoTicketId || this.cookieService.get('SSOTicketId')
        if (ssoTicketId) {
            log.trace('authenticationInterceptor', `SSOTicketId ${ssoTicketId} found, applying details to request`)

            const headers: any = req.headers
            headers['X-Page'] = this.router.url
            headers['X-Client'] = ENV.config.get('client-name')
        }
    }

    /**
     * Patches the global fetch to intercept 401 responses and redirect to /login,
     * preventing the browser from showing its native authentication dialog.
     *
     * The browser dialog is triggered when a server returns 401 with a WWW-Authenticate
     * header. By intercepting the response here we can handle it before the browser acts.
     */
    private patchFetch(): void {
        const originalFetch = window.fetch.bind(window)
        const router = this.router
        const sessionService = this.sessionService
        const cookieService = this.cookieService
        const excludedEndpoints = this.excludedEndpoints

        window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const response = await originalFetch(input, init)

            if (response.status === 401) {
                const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
                const isExcluded = excludedEndpoints.some(ep => url.includes(ep))

                if (!isExcluded) {
                    log.warn(`Received 401 from [${url}] — session likely expired. Redirecting to login.`)
                    sessionService.invalidateSession().catch(() => { log.debug('error', "Ignoring error if there is one!") })
                    cookieService.delete('SSOTicketId')
                    await router.navigate(['login'])
                }
            }

            return response
        }
    }
}
